import type {
  DataTableElement,
  DecisionTwoPathCardElement,
  KpiStripElement,
  PatternCardElement,
  TextBlockElement,
} from 'src/demo-mode/demo-elements/types'
import type { DemoChatScript } from 'src/demo-mode/types'

import {
  ACT2_PATHS,
  ADDRESS_FEED_ROOT_CAUSE,
  CARRIER_RECOVERY,
  SWEEP_90D,
} from '../data'

const k = (n: number) => `$${Math.round(n / 1000)}K`
const m = (n: number) => `$${(n / 1_000_000).toFixed(1)}M`

// ─────────── Beat 1 elements ───────────

const SWEEP_KPIS: KpiStripElement = {
  kind: 'kpi-strip',
  id: 'ia-act2-kpis',
  tiles: [
    {
      label: 'Overcharges found',
      value: k(SWEEP_90D.found),
      sublabel: '90 days · 4 carriers',
    },
    {
      label: 'Still claimable',
      value: k(SWEEP_90D.claimable),
      sublabel: 'inside dispute windows',
      status: 'good',
    },
    {
      label: 'Expired unclaimed',
      value: k(SWEEP_90D.expired),
      sublabel: 'windows already closed',
      status: 'critical',
    },
  ],
}

const CARRIER_TABLE: DataTableElement = {
  kind: 'data-table',
  id: 'ia-act2-carriers',
  subtitle: `${SWEEP_90D.carrierCount} carriers · ${SWEEP_90D.weeks} weeks · ${(SWEEP_90D.lineItems / 1_000_000).toFixed(1)}M line items`,
  columns: [
    { key: 'carrier', label: 'Carrier' },
    { key: 'spend', label: '90-day spend', align: 'right' },
    { key: 'found', label: 'Found', align: 'right' },
    { key: 'claimable', label: 'Claimable', align: 'right' },
    { key: 'expired', label: 'Expired', align: 'right' },
  ],
  rows: CARRIER_RECOVERY.map((c, i) => ({
    id: c.carrier,
    status: c.expired >= 20_000 ? 'critical' : 'warning',
    emphasize: i === 0,
    cells: {
      carrier: c.carrier,
      spend: m(c.spend90d),
      found: k(c.found),
      claimable: k(c.claimable),
      expired: k(c.expired),
    },
  })),
  takeaway: `${k(SWEEP_90D.found)} found · ${k(SWEEP_90D.claimable)} still recoverable · ${k(SWEEP_90D.expired)} already lost to closed windows.`,
}

const URGENCY: TextBlockElement = {
  kind: 'text-block',
  id: 'ia-act2-urgency',
  text: `The clock is the whole game here. **${k(SWEEP_90D.expiringSoon)} of the claimable overcharges expire in ${SWEEP_90D.expiringWindowDays} days** — after that they join the ${k(SWEEP_90D.expired)} you can no longer touch.`,
}

const ROOT_CAUSE: PatternCardElement = {
  kind: 'pattern-card',
  id: 'ia-act2-rootcause',
  tag: 'Root-cause pattern',
  title: 'Most duplicate address-correction fees trace to one feed',
  stats: [
    {
      label: 'Driver',
      value: `${ADDRESS_FEED_ROOT_CAUSE.brand} address feed`,
      emphasize: true,
    },
    {
      label: 'Share of duplicate fees',
      value: `${ADDRESS_FEED_ROOT_CAUSE.sharePct}%`,
      emphasize: true,
    },
    { label: 'Fix', value: 'Validate addresses upstream' },
  ],
  affected: [
    {
      facility: ADDRESS_FEED_ROOT_CAUSE.brand,
      status: 'orange',
      note: 'Malformed apartment/suite lines trigger carrier address-correction fees, billed twice on split shipments.',
    },
  ],
  takeaway:
    'Disputing recovers the money once; fixing the feed stops it recurring.',
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'ia-act2-b1-closer',
  text: `That's ${k(SWEEP_90D.found)} across the network, ${k(SWEEP_90D.claimable)} still on the table. **Want to see the two ways to run this from here?**`,
}

// ─────────── Beat 2 elements ───────────

const DECISION: DecisionTwoPathCardElement = {
  kind: 'decision-two-path-card',
  id: 'ia-act2-decision',
  title: 'Two ways forward',
  paths: [
    {
      label: 'Dispute what’s claimable',
      impact: `+${k(ACT2_PATHS.disputeOnly.oneTimeRecovery)} one-time`,
      impactTone: 'warning',
      lines: [
        'File the 90-day batch across all four carriers.',
        `Leakage keeps running at ~${k(ACT2_PATHS.disputeOnly.continuedLeakageMonthly)}/mo — you re-audit by hand next quarter.`,
      ],
    },
    {
      label: 'Dispute + always-on audit + root-cause fix',
      impact: `~${m(ACT2_PATHS.alwaysOn.annualizedRecovered)}/yr`,
      impactTone: 'good',
      recommended: true,
      lines: [
        `Recovered or prevented — same +${k(ACT2_PATHS.alwaysOn.oneTimeRecovery)} now, plus a weekly auditor per carrier.`,
        'Fix the address feed so the biggest bucket stops recurring.',
        'Recovery becomes a standing line item, not a project.',
      ],
    },
  ],
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'after-approvals-ia-act2-closer',
  text: 'Once approved, a weekly auditor runs for each carrier — every invoice checked against the rate card the moment it arrives, disputes drafted for your sign-off.',
}

export const recoveryAtScale: DemoChatScript = {
  id: 'recovery-at-scale',
  name: 'Invoice Audit — recovery at scale',
  description:
    'Act 2: the 90-day sweep across all four carriers. Sizes the leak (~$1M/yr), surfaces the expiring dispute window and the root-cause feed, then a two-path decision → file the batch + stand up always-on auditors.',
  triggers: [
    // Natural-language phrases that jump straight to Act 2 from a fresh chat
    // (in case a demoer skips Act 1). Substring-matched, case-insensitive.
    '90 day',
    'ninety day',
    'last 90',
    'all carrier',
    'all four carrier',
    'every carrier',
    'across carrier',
    'other carrier',
    'recovery at scale',
    'at scale',
    'how big',
    'bigger picture',
    'full picture',
    'whole picture',
    'last quarter',
    'across the network',
    'network wide',
    'network-wide',
    'sweep',
    'total opportunity',
    'total recover',
    'size the',
  ],
  events: [
    // ─────────── Beat 1: the 90-day sweep ───────────
    {
      type: 'status',
      delayMs: 200,
      message: `Auditing ${SWEEP_90D.carrierCount} carriers × ${SWEEP_90D.weeks} weeks…`,
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Same audit run against the full invoice folder and all four rate cards, 90 days back. Sorting what is still inside each carrier’s dispute window from what has already expired.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'audit_all_carriers_90d' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `${k(SWEEP_90D.found)} in overcharges across all four carriers.`,
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta: ` ${k(SWEEP_90D.claimable)} still claimable — ${k(SWEEP_90D.expired)} already expired.`,
    },
    { type: 'demo-element-add', delayMs: 600, element: SWEEP_KPIS },
    { type: 'demo-element-add', delayMs: 700, element: CARRIER_TABLE },
    { type: 'demo-element-add', delayMs: 700, element: URGENCY },
    { type: 'demo-element-add', delayMs: 700, element: ROOT_CAUSE },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Recovery at scale — 90-day sweep' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: the decision + agent-ization ───────────
    {
      type: 'text-delta',
      delayMs: 400,
      delta: 'Two ways to run this from here.',
    },
    { type: 'demo-element-add', delayMs: 600, element: DECISION },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-invoice-audit-act2-file-90day-batch',
        toolName: 'file_carrier_dispute_batch',
        actionType: 'dispute_filing',
        description: `File the 90-day dispute batch across all four carriers — ${k(SWEEP_90D.claimable)} claimable, before the next ${k(SWEEP_90D.expiringSoon)} window closes in ${SWEEP_90D.expiringWindowDays} days.`,
      },
    },
    {
      type: 'approval',
      delayMs: 500,
      proposal: {
        proposalId: 'demo-invoice-audit-act2-always-on-agent',
        toolName: 'create_invoice_audit_agents',
        actionType: 'agent_creation',
        description: `Stand up a weekly invoice-audit agent for each of the ${SWEEP_90D.carrierCount} carriers — every invoice audited against the rate card on arrival, disputes drafted automatically.`,
      },
    },
    { type: 'demo-element-add', delayMs: 700, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'Recovery at scale — decided' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-invoice-audit-act2-always-on-agent',
    },
  ],
}
